import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";
import { buildTimeEntriesQuery } from "@west-tennessee-consulting/supabase";
import { enrichTimeEntryListRow } from "./time-entries-formatters";
import { useClientRoleCheck } from "@/lib/hooks/auth/useRoleCheck";

// SECURITY (CLIENT_USER_ACCESS_SCOPE): client_user may see ONLY the
// work-transparency columns of a time entry. The REST `.select("*")` path leaks
// every internal column (billable_rate, quickbooks_*, sync_status, approval
// fields, user_id, raw start/end times, …) — RLS row-scopes but does NOT mask
// columns. So clients get an explicit safe column list; employees keep `*`.
// ticket_number/ticket_title/company_name are denormalized display LABELS.
const FULL_TIME_ENTRY_SELECT = `
          *,
          ticket:tickets(
            id,
            ticket_number,
            title,
            contact:contacts(name, company_name, email)
          ),
          user:app_users!time_entries_user_id_fkey(
            email,
            first_name,
            last_name
          )
        `;

// No user join (which WTC employee logged it is internal), no internal scalars.
const CLIENT_TIME_ENTRY_SELECT = `
          id,
          ticket_id,
          ticket_number,
          ticket_title,
          company_id,
          company_name,
          description,
          hours,
          entry_type,
          date_worked,
          ticket:tickets(
            id,
            ticket_number,
            title,
            contact:contacts(name, company_name, email)
          )
        `;

function timeEntrySelect(isClientUser: boolean): string {
  return isClientUser ? CLIENT_TIME_ENTRY_SELECT : FULL_TIME_ENTRY_SELECT;
}

export function useTimeEntries(params?: {
  page?: number;
  limit?: number;
  ticketId?: string;
  userId?: string;
  entryType?: string;
  isApproved?: boolean;
  dateFrom?: string;
  dateTo?: string;
  contactId?: string;
}) {
  const { isClientUser } = useClientRoleCheck();
  return useQuery({
    queryKey: ["time-entries", { ...params, isClientUser }],
    queryFn: async () => {
      const supabase = getSupabase();

      const { data, error } = await buildTimeEntriesQuery(supabase, {
        ticketId: params?.ticketId,
        userId: params?.userId,
        contactId: params?.contactId,
        entryType: params?.entryType,
        isApproved: params?.isApproved,
        dateFrom: params?.dateFrom ? new Date(params.dateFrom) : undefined,
        dateTo: params?.dateTo ? new Date(params.dateTo) : undefined,
        limit: params?.limit,
        page: params?.page,
      })
        .select(timeEntrySelect(isClientUser))
        .order("date_worked", { ascending: false });

      if (error) {
        console.error("Error fetching time entries:", error);
        throw new Error(error.message);
      }

      return (data || []).map(enrichTimeEntryListRow);
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useTimeEntriesPaginated(
  params?: {
    page?: number;
    limit?: number;
    ticketId?: string;
    userId?: string;
    entryType?: string;
    isApproved?: boolean;
    dateFrom?: string;
    dateTo?: string;
    contactId?: string;
    search?: string;
    syncStatus?: string;
    hours?: number;
  },
  options?: { enabled?: boolean }
) {
  const { isClientUser } = useClientRoleCheck();
  return useQuery({
    queryKey: ["time-entries-paginated", { ...params, isClientUser }],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const supabase = getSupabase();

      const countQuery = buildTimeEntriesQuery(supabase, {
        ticketId: params?.ticketId,
        userId: params?.userId,
        contactId: params?.contactId,
        entryType: params?.entryType,
        isApproved: params?.isApproved,
        dateFrom: params?.dateFrom ? new Date(params.dateFrom) : undefined,
        dateTo: params?.dateTo ? new Date(params.dateTo) : undefined,
        search: params?.search,
        syncStatus: params?.syncStatus,
        hours: params?.hours,
      });

      const { data: countData, error: countError } = await countQuery.select("id");

      if (countError) {
        console.error("Error fetching time entries count:", countError);
        throw new Error(countError.message);
      }

      let dataQuery = buildTimeEntriesQuery(supabase, {
        ticketId: params?.ticketId,
        userId: params?.userId,
        contactId: params?.contactId,
        entryType: params?.entryType,
        isApproved: params?.isApproved,
        dateFrom: params?.dateFrom ? new Date(params.dateFrom) : undefined,
        dateTo: params?.dateTo ? new Date(params.dateTo) : undefined,
        search: params?.search,
        syncStatus: params?.syncStatus,
        hours: params?.hours,
      })
        .select(timeEntrySelect(isClientUser))
        .order("date_worked", { ascending: false });

      if (params?.limit) {
        const offset = ((params.page || 1) - 1) * params.limit;
        dataQuery = dataQuery.range(offset, offset + params.limit - 1);
      }

      const { data, error: dataError } = await dataQuery;

      if (dataError) {
        console.error("Error fetching time entries data:", dataError);
        throw new Error(dataError.message);
      }

      const count = countData?.length || 0;
      const processedEntries = (data || []).map(enrichTimeEntryListRow);

      return {
        data: processedEntries,
        totalCount: count || 0,
        page: params?.page || 1,
        limit: params?.limit || 10,
        totalPages: Math.ceil((count || 0) / (params?.limit || 10)),
      };
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useTimeEntry(id: string) {
  const { isClientUser } = useClientRoleCheck();
  return useQuery({
    queryKey: ["time-entries", id, { isClientUser }],
    queryFn: async () => {
      if (!id || id.trim() === "") {
        throw new Error("Time entry ID is required");
      }

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("time_entries")
        .select(timeEntrySelect(isClientUser))
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching time entry:", error);
        throw new Error(error.message);
      }

      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!id,
  });
}
